/**
 * Depth Navigation — the spatial instrument for exploring the field.
 *
 * Four levels of depth, each earned through interaction:
 *
 *   Level 0 — OVERVIEW:  The full gravitational landscape. Zero text.
 *   Level 1 — ZONE:      Inside a category. Territory panel reveals clusters.
 *   Level 2 — CLUSTER:   Focused on a cluster. Detail panel shows sub-signals.
 *   Level 3 — NODE:      A single data point. Provenance card shows source.
 *
 * Not a modal. Not a page transition.
 * A spatial shift — like adjusting the focal length of an instrument.
 *
 * Escape always goes back one level. The field always provides context.
 */

import * as THREE from 'three';
import type { FieldCamera } from './camera';
import type { FieldLabels } from './labels';
import { useAuraStore, type DepthLevel, type ZoneContext, type ClusterDetail, type Trend } from '../state/store';
import type { SyntheticField, SyntheticNode } from '../data/synthetic';
import { attachTooltip, hideTooltip } from '../interaction/tooltip';

// ─── TOOLTIP CONTENT ──────────────────────────────────────────

const TREND_TOOLTIPS: Record<string, string> = {
  rising: 'Gaining momentum — signal strength increasing over time',
  stable: 'Consistent presence — neither growing nor declining',
  fading: 'Declining strength — signal may be losing relevance',
  emerging: 'Newly detected — too early to confirm trajectory',
};

const PROVENANCE_TOOLTIPS: Record<string, string> = {
  'Signal Strength': 'How strong this data point is relative to the field',
  'Trend': 'Whether this signal is rising, stable, fading, or emerging',
  'Source': 'The signal class that detected this data point',
  'Last Updated': 'When this signal was last refreshed from its source',
  'Confidence': 'Statistical confidence in this signal\'s classification',
};

interface ZoneInfo {
  name: string;
  center: THREE.Vector3;
  color: THREE.Color;
  colorCSS: string;
  nodeCount: number;
  clusters: { name: string; intensity: number; trend: Trend; nodeIndex: number; position: THREE.Vector3 }[];
}

export class ZoneNavigation {
  private zones: ZoneInfo[] = [];
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private pointerDownPos: { x: number; y: number } | null = null;
  private field: SyntheticField;

  // DOM elements
  private territoryPanel: HTMLElement | null;
  private territoryZoneName: HTMLElement | null;
  private territoryClusters: HTMLElement | null;
  private clusterDetail: HTMLElement | null;
  private clusterBack: HTMLElement | null;
  private clusterFocusName: HTMLElement | null;
  private clusterFocusCategory: HTMLElement | null;
  private clusterSubsignals: HTMLElement | null;
  private clusterConnections: HTMLElement | null;
  private provenanceCard: HTMLElement | null;
  private provenanceName: HTMLElement | null;
  private provenanceCategory: HTMLElement | null;
  private provenanceRows: HTMLElement | null;
  private depthIndicator: HTMLElement | null;

  constructor(
    private container: HTMLElement,
    private perspCamera: THREE.PerspectiveCamera,
    private fieldCamera: FieldCamera,
    private labels: FieldLabels,
    field: SyntheticField,
  ) {
    this.field = field;

    this.zones = labels.getCategories().map((cat) => {
      const r = Math.round(cat.color.r * 255);
      const g = Math.round(cat.color.g * 255);
      const b = Math.round(cat.color.b * 255);

      return {
        name: cat.name,
        center: cat.center.clone(),
        color: cat.color.clone(),
        colorCSS: `rgb(${r}, ${g}, ${b})`,
        nodeCount: cat.nodeCount,
        clusters: cat.clusters.map((c) => {
          // Find the node index in the field data
          const nodeIdx = field.nodes.findIndex(
            (n) => n.name === c.name && n.category === cat.name
          );
          return {
            name: c.name,
            intensity: c.intensity,
            trend: (field.nodes[nodeIdx]?.trend ?? 'stable') as Trend,
            nodeIndex: nodeIdx,
            position: c.position.clone(),
          };
        }),
      };
    });

    // Cache DOM elements
    this.territoryPanel = document.getElementById('territory-panel');
    this.territoryZoneName = document.getElementById('territory-zone-name');
    this.territoryClusters = document.getElementById('territory-clusters');
    this.clusterDetail = document.getElementById('cluster-detail');
    this.clusterBack = document.getElementById('cluster-back');
    this.clusterFocusName = document.getElementById('cluster-focus-name');
    this.clusterFocusCategory = document.getElementById('cluster-focus-category');
    this.clusterSubsignals = document.getElementById('cluster-subsignals');
    this.clusterConnections = document.getElementById('cluster-connections');
    this.provenanceCard = document.getElementById('provenance-card');
    this.provenanceName = document.getElementById('provenance-name');
    this.provenanceCategory = document.getElementById('provenance-category');
    this.provenanceRows = document.getElementById('provenance-rows');
    this.depthIndicator = document.getElementById('depth-indicator');

    this.bindEvents();
  }

  private bindEvents() {
    this.container.addEventListener('pointerdown', this.onPointerDown, true);
    this.container.addEventListener('click', this.onClick, true);
    document.addEventListener('keydown', this.onKeyDown);

    // Back button in cluster detail
    this.clusterBack?.addEventListener('click', () => {
      const store = useAuraStore.getState();
      if (store.depthLevel === 'cluster') {
        this.ascendToZone();
      }
    });
  }

  private onPointerDown = (e: PointerEvent) => {
    this.pointerDownPos = { x: e.clientX, y: e.clientY };
  };

  private onClick = (e: MouseEvent) => {
    // Only genuine clicks (not drags)
    if (!this.pointerDownPos) return;
    const dx = e.clientX - this.pointerDownPos.x;
    const dy = e.clientY - this.pointerDownPos.y;
    this.pointerDownPos = null;
    if (Math.sqrt(dx * dx + dy * dy) > 6) return;

    const store = useAuraStore.getState();

    // At overview level — clicking category labels enters a zone
    if (store.depthLevel === 'overview') {
      this.mouse.set(
        (e.clientX / this.container.clientWidth) * 2 - 1,
        -(e.clientY / this.container.clientHeight) * 2 + 1,
      );
      this.raycaster.setFromCamera(this.mouse, this.perspCamera);

      const sprites = this.labels.getCategorySprites();
      const intersects = this.raycaster.intersectObjects(sprites);

      if (intersects.length > 0) {
        const hitSprite = intersects[0].object;
        const idx = sprites.indexOf(hitSprite as THREE.Sprite);
        if (idx >= 0 && this.zones[idx]) {
          e.stopImmediatePropagation();
          this.diveToZone(this.zones[idx]);
        }
      }
    }
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;

    const store = useAuraStore.getState();

    switch (store.depthLevel) {
      case 'node':
        this.ascendToCluster();
        break;
      case 'cluster':
        this.ascendToZone();
        break;
      case 'zone':
        this.ascendToOverview();
        break;
      default:
        break;
    }
  };

  // ─── Level 0 → Level 1: Dive into zone ──────────────────────

  diveToZone(zone: ZoneInfo) {
    const store = useAuraStore.getState();

    store.setDepth('zone');
    store.setActiveZone({
      name: zone.name,
      color: zone.colorCSS,
      clusters: zone.clusters.map((c) => ({
        name: c.name,
        weight: c.intensity,
        trend: c.trend,
        nodeIndex: c.nodeIndex,
      })),
    });
    store.setActiveCluster(null);

    // Camera dives into zone
    this.fieldCamera.diveTo(zone.center, 280);
    this.labels.setActiveZone(zone.name);

    // Populate & show territory panel
    this.showTerritoryPanel(zone);
    this.hideClusterDetail();
    this.hideProvenanceCard();
    this.updateDepthIndicator();
  }

  // ─── Level 1 → Level 2: Focus on cluster ─────────────────────

  diveToCluster(zone: ZoneInfo, clusterIndex: number) {
    const cluster = zone.clusters[clusterIndex];
    if (!cluster) return;

    const store = useAuraStore.getState();
    const node = this.field.nodes[cluster.nodeIndex];

    // Find sub-signals (satellite nodes connected to this cluster via edges)
    const connectedIndices = new Set<number>();
    if (cluster.nodeIndex >= 0) {
      for (const edge of this.field.edges) {
        if (edge.source === cluster.nodeIndex && edge.target !== cluster.nodeIndex) {
          connectedIndices.add(edge.target);
        }
        if (edge.target === cluster.nodeIndex && edge.source !== cluster.nodeIndex) {
          connectedIndices.add(edge.source);
        }
      }
    }

    const subSignals = this.field.nodes
      .filter((n, idx) =>
        n.category === zone.name &&
        n.id !== node?.id &&
        (connectedIndices.has(idx) || n.name.startsWith(cluster.name))
      )
      .map((n) => ({
        name: n.name.replace(` (sub-signal `, ' #').replace(')', ''),
        intensity: n.intensity,
        trend: n.trend as Trend,
      }));

    // Find cross-connections from edges
    const connections: { name: string; category: string; weight: number }[] = [];
    if (cluster.nodeIndex >= 0) {
      for (const edge of this.field.edges) {
        let otherIdx = -1;
        if (edge.source === cluster.nodeIndex) otherIdx = edge.target;
        if (edge.target === cluster.nodeIndex) otherIdx = edge.source;
        if (otherIdx >= 0 && otherIdx < this.field.nodes.length) {
          const other = this.field.nodes[otherIdx];
          if (other.category !== zone.name && other.id.startsWith('cluster-')) {
            connections.push({
              name: other.name,
              category: other.category,
              weight: edge.weight,
            });
          }
        }
      }
    }

    store.setDepth('cluster');
    store.setActiveCluster({
      name: cluster.name,
      category: zone.name,
      weight: cluster.intensity,
      trend: cluster.trend,
      color: zone.colorCSS,
      nodeIndex: cluster.nodeIndex,
      subSignals,
      connections,
    });

    // Camera focuses on cluster
    this.fieldCamera.diveTo(cluster.position, 120);

    // Show cluster detail, hide territory
    this.hideTerritoryPanel();
    this.showClusterDetail(zone);
    this.hideProvenanceCard();
    this.updateDepthIndicator();
  }

  // ─── Level 2 → Level 3: Focus on node ────────────────────────

  diveToNode(nodeIndex: number) {
    const node = this.field.nodes[nodeIndex];
    if (!node) return;

    const store = useAuraStore.getState();
    store.setDepth('node');

    // Camera closes in
    const pos = new THREE.Vector3(...node.position);
    this.fieldCamera.diveTo(pos, 40);

    // Show provenance card
    this.showProvenanceCard(node);
    this.updateDepthIndicator();
  }

  // ─── Ascent: back one level ───────────────────────────────────

  private ascendToCluster() {
    const store = useAuraStore.getState();
    const zone = store.activeZone;

    store.setDepth('cluster');
    this.fieldCamera.undive();
    this.hideProvenanceCard();
    this.updateDepthIndicator();

    // Re-show cluster detail if we have context
    if (zone) {
      const zoneInfo = this.zones.find((z) => z.name === zone.name);
      if (zoneInfo) {
        this.showClusterDetail(zoneInfo);
      }
    }
  }

  private ascendToZone() {
    const store = useAuraStore.getState();
    const zone = store.activeZone;

    store.setDepth('zone');
    store.setActiveCluster(null);
    this.fieldCamera.undive();
    this.hideClusterDetail();
    this.hideProvenanceCard();

    // Re-show territory panel
    if (zone) {
      const zoneInfo = this.zones.find((z) => z.name === zone.name);
      if (zoneInfo) {
        this.showTerritoryPanel(zoneInfo);
      }
    }
    this.updateDepthIndicator();
  }

  private ascendToOverview() {
    const store = useAuraStore.getState();

    store.setDepth('overview');
    store.setActiveZone(null);
    store.setActiveCluster(null);
    this.fieldCamera.undive();
    this.labels.setActiveZone(null);

    this.hideTerritoryPanel();
    this.hideClusterDetail();
    this.hideProvenanceCard();
    this.updateDepthIndicator();
  }

  // ─── Territory Panel (Level 1) ────────────────────────────────

  private showTerritoryPanel(zone: ZoneInfo) {
    if (!this.territoryPanel || !this.territoryZoneName || !this.territoryClusters) return;

    this.territoryZoneName.textContent = zone.name;
    this.territoryZoneName.style.color = zone.colorCSS;
    attachTooltip(this.territoryZoneName, `Signal zone — ${zone.clusters.length} clusters, ${zone.nodeCount} total data points`);

    // Build cluster list
    const maxWeight = Math.max(...zone.clusters.map((c) => c.intensity));
    this.territoryClusters.innerHTML = '';

    // Sort clusters by weight (heaviest first)
    const sorted = [...zone.clusters].sort((a, b) => b.intensity - a.intensity);

    sorted.forEach((cluster, i) => {
      const el = document.createElement('div');
      el.className = 'territory-cluster';
      el.dataset.clusterIndex = String(zone.clusters.indexOf(cluster));

      const pct = Math.round((cluster.intensity / maxWeight) * 100);
      const trendLabel = cluster.trend;
      const trendClass = `territory-cluster-trend ${trendLabel}`;

      el.innerHTML = `
        <div class="territory-cluster-header">
          <span class="territory-cluster-name">${cluster.name}</span>
          <span class="${trendClass}">${trendLabel}</span>
        </div>
        <div class="territory-weight-track">
          <div class="territory-weight-bar" style="width: ${pct}%; background: ${zone.colorCSS}; opacity: 0.6;"></div>
        </div>
      `;

      // Tooltips: trend badge and weight bar
      const trendBadge = el.querySelector('.territory-cluster-trend') as HTMLElement;
      if (trendBadge && TREND_TOOLTIPS[trendLabel]) {
        attachTooltip(trendBadge, TREND_TOOLTIPS[trendLabel]);
      }
      const weightBar = el.querySelector('.territory-weight-track') as HTMLElement;
      if (weightBar) {
        attachTooltip(weightBar, `${pct}% signal strength relative to the strongest cluster in this zone`);
      }
      attachTooltip(el.querySelector('.territory-cluster-name') as HTMLElement, 'Click to explore sub-signals and connections');

      // Click to dive to cluster (Level 2)
      el.addEventListener('click', () => {
        const idx = zone.clusters.indexOf(cluster);
        this.diveToCluster(zone, idx);
      });

      this.territoryClusters!.appendChild(el);
    });

    // Animate weight bars
    requestAnimationFrame(() => {
      this.territoryPanel!.classList.add('visible');
    });
  }

  private hideTerritoryPanel() {
    this.territoryPanel?.classList.remove('visible');
  }

  // ─── Cluster Detail (Level 2) ─────────────────────────────────

  private showClusterDetail(zone: ZoneInfo) {
    const store = useAuraStore.getState();
    const cluster = store.activeCluster;
    if (!cluster || !this.clusterDetail || !this.clusterFocusName || !this.clusterFocusCategory) return;

    this.clusterFocusName!.textContent = cluster.name;
    this.clusterFocusName!.style.color = cluster.color;
    this.clusterFocusCategory!.textContent = cluster.category;
    attachTooltip(this.clusterFocusName!, 'Cluster — a gravitational grouping of related signals');
    attachTooltip(this.clusterFocusCategory!, 'Intelligence domain this cluster belongs to');

    // Finding 7: add "what is this" description below category label
    let descEl = document.getElementById('cluster-focus-description');
    if (!descEl) {
      descEl = document.createElement('div');
      descEl.id = 'cluster-focus-description';
      descEl.className = 'cluster-focus-description';
      this.clusterFocusCategory!.insertAdjacentElement('afterend', descEl);
    }
    // Brief description
    descEl.textContent = ``;
    this.loadClusterBrief(cluster, descEl);

    if (this.clusterBack) {
      attachTooltip(this.clusterBack, 'Return to zone view (or press Escape)');
    }

    // Hide the legacy sub-signals section — replaced by copy-agent brief
    if (this.clusterSubsignals) {
      this.clusterSubsignals.innerHTML = '';
      const parent = this.clusterSubsignals.closest('.cluster-section-title')?.parentElement;
    }

    // Cross-connections
    if (this.clusterConnections) {
      this.clusterConnections.innerHTML = '';
      if (cluster.connections.length === 0) {
        this.clusterConnections.innerHTML = '<div class="cluster-connection"><span class="cluster-connection-name" style="opacity:0.3">No cross-category connections</span></div>';
      } else {
        for (const conn of cluster.connections) {
          // Find the zone color for this connection
          const connZone = this.zones.find((z) => z.name === conn.category);
          const dotColor = connZone?.colorCSS ?? 'rgba(255,255,255,0.3)';

          const el = document.createElement('div');
          el.className = 'cluster-connection';
          const weightPct = Math.round(conn.weight * 100);
          el.innerHTML = `
            <div class="cluster-connection-dot" style="background: ${dotColor}"></div>
            <span class="cluster-connection-name">${conn.name}</span>
            <span class="cluster-connection-cat">${conn.category}</span>
          `;
          // Tooltip on connection — explains the cross-zone link
          attachTooltip(el, `${weightPct}% connection strength to ${conn.category} zone — signals that co-occur across categories`);
          this.clusterConnections.appendChild(el);
        }
      }
    }

    requestAnimationFrame(() => {
      this.clusterDetail!.classList.add('visible');
    });
  }

  private hideClusterDetail() {
    this.clusterDetail?.classList.remove('visible');
  }

  // ─── Provenance Card (Level 3) ────────────────────────────────

  private showProvenanceCard(node: SyntheticNode) {
    if (!this.provenanceCard || !this.provenanceName || !this.provenanceCategory || !this.provenanceRows) return;

    this.provenanceName.textContent = node.name;
    this.provenanceCategory.textContent = node.category;
    attachTooltip(this.provenanceName, 'Individual data point — the smallest unit of intelligence in the field');
    attachTooltip(this.provenanceCategory, 'The signal category this node belongs to');

    // Synthesized provenance data (this would come from real data in production)
    const signalPct = Math.round(node.intensity * 100);
    const sourceTypes = ['SERP Analysis', 'Social Listening', 'Cultural Signal', 'Behavioral Data', 'Industry Report'];
    const sourceType = sourceTypes[Math.abs(node.id.charCodeAt(0)) % sourceTypes.length];
    const daysAgo = Math.floor(Math.abs(node.id.charCodeAt(1)) % 30) + 1;
    const confidence = Math.round(60 + node.intensity * 35);

    this.provenanceRows.innerHTML = '';
    const rows = [
      { label: 'Signal Strength', value: `${signalPct}%` },
      { label: 'Trend', value: node.trend },
      { label: 'Source', value: sourceType },
      { label: 'Last Updated', value: `${daysAgo} days ago` },
      { label: 'Confidence', value: `${confidence}%` },
    ];

    for (const row of rows) {
      const el = document.createElement('div');
      el.className = 'provenance-row';
      el.innerHTML = `
        <span class="provenance-label">${row.label}</span>
        <span class="provenance-value">${row.value}</span>
      `;
      // Tooltip on each provenance label
      const labelEl = el.querySelector('.provenance-label') as HTMLElement;
      if (labelEl && PROVENANCE_TOOLTIPS[row.label]) {
        attachTooltip(labelEl, PROVENANCE_TOOLTIPS[row.label]);
      }
      this.provenanceRows.appendChild(el);
    }

    requestAnimationFrame(() => {
      this.provenanceCard!.classList.add('visible');
    });
  }

  private hideProvenanceCard() {
    this.provenanceCard?.classList.remove('visible');
  }

  // ─── Depth Indicator (breadcrumb) ─────────────────────────────

  private updateDepthIndicator() {
    if (!this.depthIndicator) return;

    const store = useAuraStore.getState();
    const level = store.depthLevel;

    if (level === 'overview') {
      this.depthIndicator.classList.remove('visible');
      return;
    }

    this.depthIndicator.classList.add('visible');
    const crumbs: string[] = [];

    // Overview crumb
    crumbs.push(`<span class="depth-crumb" data-depth="overview">Overview</span>`);

    if (store.activeZone) {
      if (level === 'zone') {
        crumbs.push(`<span class="depth-separator">›</span>`);
        crumbs.push(`<span class="depth-current">${store.activeZone.name}</span>`);
      } else {
        crumbs.push(`<span class="depth-separator">›</span>`);
        crumbs.push(`<span class="depth-crumb" data-depth="zone">${store.activeZone.name}</span>`);
      }
    }

    if (store.activeCluster) {
      if (level === 'cluster') {
        crumbs.push(`<span class="depth-separator">›</span>`);
        crumbs.push(`<span class="depth-current">${store.activeCluster.name}</span>`);
      } else {
        crumbs.push(`<span class="depth-separator">›</span>`);
        crumbs.push(`<span class="depth-crumb" data-depth="cluster">${store.activeCluster.name}</span>`);
      }
    }

    if (level === 'node') {
      const node = store.contextData;
      if (node) {
        crumbs.push(`<span class="depth-separator">›</span>`);
        crumbs.push(`<span class="depth-current">${node.name}</span>`);
      }
    }

    this.depthIndicator.innerHTML = crumbs.join('');

    // Tooltips on breadcrumbs
    this.depthIndicator.querySelectorAll('.depth-crumb').forEach((el) => {
      const depth = (el as HTMLElement).dataset.depth;
      if (depth === 'overview') {
        attachTooltip(el as HTMLElement, 'Return to full field overview');
      } else if (depth === 'zone') {
        attachTooltip(el as HTMLElement, 'Return to zone territory view');
      } else if (depth === 'cluster') {
        attachTooltip(el as HTMLElement, 'Return to cluster detail view');
      }
    });

    // Tooltip on current depth indicator (non-clickable)
    const currentEl = this.depthIndicator.querySelector('.depth-current') as HTMLElement;
    if (currentEl) {
      attachTooltip(currentEl, 'Current depth level — press Escape to go back');
    }

    // Bind click handlers on breadcrumbs
    this.depthIndicator.querySelectorAll('.depth-crumb').forEach((el) => {
      el.addEventListener('click', () => {
        const depth = (el as HTMLElement).dataset.depth;
        if (depth === 'overview') {
          this.ascendToOverview();
        } else if (depth === 'zone') {
          // If we're at node level, need two undives to get to zone
          const store = useAuraStore.getState();
          if (store.depthLevel === 'node') {
            this.fieldCamera.undive(); // cluster
            this.fieldCamera.undive(); // zone
            store.setDepth('zone');
            store.setActiveCluster(null);
            this.hideClusterDetail();
            this.hideProvenanceCard();
            const zoneInfo = this.zones.find((z) => z.name === store.activeZone?.name);
            if (zoneInfo) this.showTerritoryPanel(zoneInfo);
            this.updateDepthIndicator();
          } else {
            this.ascendToZone();
          }
        } else if (depth === 'cluster') {
          this.ascendToCluster();
        }
      });
    });
  }

  // ─── Public API ──────────────────────────────────────────────

  /**
   * Force-reset to overview from any depth level.
   * Used by the query system to clear all panels before taking over.
   * Clears the camera stack entirely (no undive chain needed).
   */
  forceOverview() {
    const store = useAuraStore.getState();

    store.setDepth('overview');
    store.setActiveZone(null);
    store.setActiveCluster(null);
    store.clearCameraStack();
    this.labels.setActiveZone(null);

    this.hideTerritoryPanel();
    this.hideClusterDetail();
    this.hideProvenanceCard();
    this.updateDepthIndicator();
  }

  /**
   * Enter a zone's context (panels + state) without moving the camera.
   * Used by the query system — it controls the camera independently.
   */
  enterZoneContext(categoryName: string) {
    const zone = this.zones.find((z) => z.name === categoryName);
    if (!zone) return;

    const store = useAuraStore.getState();

    store.setDepth('zone');
    store.setActiveZone({
      name: zone.name,
      color: zone.colorCSS,
      clusters: zone.clusters.map((c) => ({
        name: c.name,
        weight: c.intensity,
        trend: c.trend,
        nodeIndex: c.nodeIndex,
      })),
    });
    store.setActiveCluster(null);
    this.labels.setActiveZone(zone.name);

    this.showTerritoryPanel(zone);
    this.hideClusterDetail();
    this.hideProvenanceCard();
    this.updateDepthIndicator();
  }

  getActiveZone(): string | null {
    return useAuraStore.getState().activeZone?.name ?? null;
  }

  getDepthLevel(): DepthLevel {
    return useAuraStore.getState().depthLevel;
  }

  /** Called by external systems (e.g., selection) to trigger node focus */
  focusNode(nodeIndex: number) {
    const store = useAuraStore.getState();
    const node = this.field.nodes[nodeIndex];
    if (!node) return;

    // If we're not in a zone yet, enter the zone first
    if (store.depthLevel === 'overview') {
      const zone = this.zones.find((z) => z.name === node.category);
      if (zone) {
        this.diveToZone(zone);
        // Slight delay then dive to cluster
        setTimeout(() => {
          const clusterIdx = zone.clusters.findIndex((c) => c.nodeIndex === nodeIndex);
          if (clusterIdx >= 0) {
            this.diveToCluster(zone, clusterIdx);
          }
        }, 400);
      }
    } else if (store.depthLevel === 'zone') {
      // Already in zone, dive to cluster if this is a cluster node
      const zone = this.zones.find((z) => z.name === node.category);
      if (zone) {
        const clusterIdx = zone.clusters.findIndex((c) => c.nodeIndex === nodeIndex);
        if (clusterIdx >= 0) {
          this.diveToCluster(zone, clusterIdx);
        } else {
          this.diveToNode(nodeIndex);
        }
      }
    } else {
      // Already deep — just focus the node
      this.diveToNode(nodeIndex);
    }
  }

  // ── Copy Agent Brief ──────────────────────────────────────────────────────

  private async loadClusterBrief(cluster: any, el: HTMLElement) {
    const GATEWAY = (import.meta as any).env?.VITE_API_URL || '';
    if (!GATEWAY) {
      el.textContent = 'A cluster of web, news, and events signals — grouped by shared entities and themes.';
      return;
    }

    // Get the actual Neon UUID from the field's node array
    const neonId = this.field.nodes[cluster.nodeIndex]?.id || '';

    el.innerHTML = '<span style="color:rgba(255,255,255,0.20);font-style:italic;font-size:11px">Reading the field…</span>';

    try {
      // Fetch real signal data using the Neon UUID
      const clusterRes = neonId
        ? await fetch(`${GATEWAY}/api/clusters/${neonId}`, { signal: AbortSignal.timeout(5000) }).catch(() => null)
        : null;

      const clusterData = clusterRes?.ok ? await clusterRes.json() : null;
      const signals = clusterData?.signals?.slice(0, 6) || [];
      const clusterEntities = clusterData?.cluster?.entities || [];
      const clusterDesc = clusterData?.cluster?.description || '';

      const res = await fetch(`${GATEWAY}/api/ai/copy-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cluster: {
            id: neonId,
            topic: cluster.name,
            description: clusterDesc,
            entities: clusterEntities,
          },
          signals,
          format: 'cluster',
        }),
        signal: AbortSignal.timeout(20_000),
      });

      if (!res.ok) throw new Error('copy-brief failed');
      const data = await res.json() as any;
      el.textContent = data.brief || 'A cluster of signals from web, news, and events.';

      // Add "Open a Field →" CTA after the brief
      const ctaEl = document.createElement('button');
      ctaEl.textContent = 'Open a Field →';
      ctaEl.style.cssText = `
        display: block; margin-top: 14px; padding: 8px 16px;
        background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
        border-radius: 4px; color: rgba(255,255,255,0.70); font-size: 11px;
        font-weight: 500; font-family: 'Inter', sans-serif; cursor: pointer;
        letter-spacing: 0.03em; transition: background 0.2s ease;
      `;
      ctaEl.onmouseenter = () => { ctaEl.style.background = 'rgba(255,255,255,0.13)'; };
      ctaEl.onmouseleave = () => { ctaEl.style.background = 'rgba(255,255,255,0.07)'; };
      ctaEl.onclick = () => { useAuraStore.getState().setMode('field'); };
      el.insertAdjacentElement('afterend', ctaEl);

    } catch {
      el.textContent = 'A cluster of signals from web, news, and events — grouped by shared entities and themes.';
    }
  }

  dispose() {
    this.container.removeEventListener('pointerdown', this.onPointerDown, true);
    this.container.removeEventListener('click', this.onClick, true);
    document.removeEventListener('keydown', this.onKeyDown);
  }
}
